/**
 * MyProfilePage (v37 — May 21, 2026)
 *
 * Self-service profile + KYC editor. Users can update their own personal details,
 * KYC fields (PAN, Aadhaar, address, emergency contact, blood group) and
 * celebration dates (DOB + wedding anniversary). Sensitive fields like salary,
 * employment type, role, status are READ-ONLY here — only HR/Admin can change those.
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { staffAPI, v37API } from '../lib/api';
import { useAuth } from '../lib/auth';
import { toast } from 'sonner';
import { Loader2, ShieldCheck, Calendar as CalendarIcon, Heart, Cake, BadgeCheck, Phone, MapPin, Droplet, FileBadge, ArrowLeft, Camera, Trash2, User as UserIcon } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import PhoneInput from '../components/PhoneInput';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { cn } from '../lib/utils';

const BLOOD_GROUPS = ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'];

const MyProfilePage = () => {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [profile, setProfile] = useState(null);
  const [kyc, setKyc] = useState(null);
  const [form, setForm] = useState({
    first_name: '', last_name: '', phone: '',
    date_of_birth: '', wedding_anniversary: '',
    pan: '', aadhaar: '', address: '',
    emergency_contact: '', emergency_phone: '',
    blood_group: '',
  });

  const enabled = !!user && !user.is_super_admin && !!user.hospital_id;

  useEffect(() => {
    if (!enabled) { setLoading(false); return; }
    (async () => {
      try {
        // Find own staff_profile via staff search by user_id
        const meRes = await api.get('/auth/me');
        const me = meRes.data;
        const allStaffRes = await staffAPI.list();
        const own = (allStaffRes.data || []).find(s => s.user_id === me.id || (s.user && s.user.id === me.id));
        setProfile(own);
        const kycRes = await v37API.kycStatus();
        setKyc(kycRes.data);
        if (own) {
          setForm({
            first_name: me.first_name || '',
            last_name: me.last_name || '',
            phone: me.phone || '',
            date_of_birth: own.date_of_birth || '',
            wedding_anniversary: own.wedding_anniversary || '',
            pan: own.pan || '',
            aadhaar: own.aadhaar || '',
            address: own.address || '',
            emergency_contact: own.emergency_contact || '',
            emergency_phone: own.emergency_phone || '',
            blood_group: own.blood_group || '',
          });
        }
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Failed to load profile');
      } finally {
        setLoading(false);
      }
    })();
  }, [enabled]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const refreshKyc = async () => {
    try { const r = await v37API.kycStatus(); setKyc(r.data); } catch { /* noop */ }
  };

  const handlePhotoChange = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!['image/jpeg', 'image/jpg', 'image/png', 'image/webp'].includes(f.type)) {
      toast.error('Please pick a JPEG, PNG or WEBP image');
      return;
    }
    if (f.size > 4 * 1024 * 1024) {
      toast.error('Image too large (max 4MB)');
      return;
    }
    setUploadingPhoto(true);
    try {
      const r = await staffAPI.uploadMyPhoto(f);
      setProfile(p => ({ ...(p || {}), profile_photo_url: r.data.profile_photo_url }));
      toast.success('Profile photo updated');
      await refreshKyc();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Photo upload failed');
    } finally {
      setUploadingPhoto(false);
      // reset input so picking the same file again still triggers change
      e.target.value = '';
    }
  };

  const handlePhotoRemove = async () => {
    if (!profile?.profile_photo_url) return;
    if (!window.confirm('Remove your profile photo?')) return;
    setUploadingPhoto(true);
    try {
      await staffAPI.deleteMyPhoto();
      setProfile(p => ({ ...(p || {}), profile_photo_url: null }));
      toast.success('Photo removed');
      await refreshKyc();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to remove');
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Strip empty strings → null so backend doesn't store "" everywhere
      const payload = Object.fromEntries(Object.entries(form).map(([k, v]) => [k, v === '' ? null : v]));
      await staffAPI.updateMyProfile(payload);
      toast.success('Profile updated. Thank you!');
      // Refresh KYC status
      const kycRes = await v37API.kycStatus();
      setKyc(kycRes.data);
      try { await refreshUser(); } catch { /* noop */ }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (!enabled) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 p-8 text-center text-sm text-slate-500">
        My Profile is not available for Super Admin accounts.
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16 text-slate-400 gap-2">
        <Loader2 className="animate-spin" size={20} /> Loading your profile…
      </div>
    );
  }

  return (
    <div className="space-y-5 max-w-4xl" data-testid="my-profile-page">
      <div className="flex items-center justify-between">
        <div>
          <button onClick={() => navigate(-1)} className="text-xs text-slate-500 hover:text-slate-700 inline-flex items-center gap-1 mb-1" data-testid="my-profile-back">
            <ArrowLeft size={12} /> Back
          </button>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">My Profile</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">Keep your details up to date — used for KYC + birthday/anniversary greetings.</p>
        </div>
        {kyc && (
          <div className={cn(
            'inline-flex items-center gap-2 px-3 py-2 rounded-xl border',
            kyc.is_complete
              ? 'bg-emerald-50 border-emerald-200 text-emerald-700'
              : 'bg-amber-50 border-amber-200 text-amber-700'
          )} data-testid="kyc-completion-pill">
            <ShieldCheck size={16} />
            <div className="text-xs">
              <div className="font-bold">{kyc.completion_pct}% complete</div>
              <div className="opacity-80">
                {kyc.is_complete ? 'All set!' : `${(kyc.missing_required?.length || 0) + (kyc.missing_encouraged?.length || 0)} field(s) pending`}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Profile Photo (v38) */}
      <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 p-5" data-testid="profile-photo-card">
        <h2 className="text-sm font-bold text-slate-800 dark:text-slate-100 mb-3 flex items-center gap-2">
          <Camera size={14} className="text-violet-600" /> Profile Photo
          <span className="text-[10px] font-normal text-slate-400 ml-1">Used on your ID card, Staff 360 and the team directory.</span>
        </h2>
        <div className="flex items-center gap-5">
          <div className="h-24 w-24 rounded-2xl bg-slate-100 dark:bg-slate-800 overflow-hidden flex items-center justify-center border border-slate-200 dark:border-slate-700">
            {profile?.profile_photo_url ? (
              <img
                src={`${process.env.REACT_APP_BACKEND_URL}${profile.profile_photo_url}`}
                alt="Profile"
                className="w-full h-full object-cover"
                data-testid="profile-photo-preview"
              />
            ) : (
              <UserIcon size={36} className="text-slate-400" />
            )}
          </div>
          <div className="space-y-2">
            <label className="inline-flex items-center gap-2 cursor-pointer rounded-lg px-3 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold transition" data-testid="profile-photo-pick">
              <Camera size={14} />
              {uploadingPhoto ? 'Uploading…' : (profile?.profile_photo_url ? 'Replace Photo' : 'Upload Photo')}
              <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={handlePhotoChange} disabled={uploadingPhoto} data-testid="profile-photo-input" />
            </label>
            {profile?.profile_photo_url && (
              <Button type="button" variant="ghost" size="sm" className="text-xs text-rose-600 hover:bg-rose-50" onClick={handlePhotoRemove} disabled={uploadingPhoto} data-testid="profile-photo-remove">
                <Trash2 size={12} className="mr-1" /> Remove
              </Button>
            )}
            <p className="text-[10px] text-slate-400">JPEG / PNG / WEBP · max 4MB</p>
          </div>
        </div>
      </div>

      {/* Identity (read-mostly) */}
      <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 p-5">
        <h2 className="text-sm font-bold text-slate-800 dark:text-slate-100 mb-3 flex items-center gap-2">
          <BadgeCheck size={14} className="text-blue-600" /> Identity
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div><Label>First Name</Label><Input value={form.first_name} onChange={e => set('first_name', e.target.value)} data-testid="profile-first-name" /></div>
          <div><Label>Last Name</Label><Input value={form.last_name} onChange={e => set('last_name', e.target.value)} data-testid="profile-last-name" /></div>
          <div><Label>Phone</Label><PhoneInput value={form.phone} onChange={v => set('phone', v)} testid="profile-phone" /></div>
        </div>
        {profile && (
          <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-2 text-[11px]">
            <div className="bg-slate-50 dark:bg-slate-800/50 rounded-lg px-3 py-1.5"><span className="text-slate-400 block">Employee ID</span><span className="font-medium font-mono">{profile.employee_code}</span></div>
            <div className="bg-slate-50 dark:bg-slate-800/50 rounded-lg px-3 py-1.5"><span className="text-slate-400 block">Department</span><span className="font-medium">{profile.department}</span></div>
            <div className="bg-slate-50 dark:bg-slate-800/50 rounded-lg px-3 py-1.5"><span className="text-slate-400 block">Designation</span><span className="font-medium">{profile.designation}</span></div>
            <div className="bg-slate-50 dark:bg-slate-800/50 rounded-lg px-3 py-1.5"><span className="text-slate-400 block">Joined</span><span className="font-medium">{profile.joining_date}</span></div>
          </div>
        )}
      </div>

      {/* Celebration Dates */}
      <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 p-5">
        <h2 className="text-sm font-bold text-slate-800 dark:text-slate-100 mb-1 flex items-center gap-2">
          <Cake size={14} className="text-pink-600" /> Celebration Dates
        </h2>
        <p className="text-[11px] text-slate-500 mb-3">We&apos;ll wish you on your special day. Required for the team celebrations widget.</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <Label className="flex items-center gap-1.5"><Cake size={12} /> Date of Birth</Label>
            <Input type="date" value={form.date_of_birth || ''} onChange={e => set('date_of_birth', e.target.value)} data-testid="profile-dob" />
          </div>
          <div>
            <Label className="flex items-center gap-1.5"><Heart size={12} /> Wedding Anniversary</Label>
            <Input type="date" value={form.wedding_anniversary || ''} onChange={e => set('wedding_anniversary', e.target.value)} data-testid="profile-anniv" />
          </div>
        </div>
      </div>

      {/* KYC */}
      <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 p-5">
        <h2 className="text-sm font-bold text-slate-800 dark:text-slate-100 mb-3 flex items-center gap-2">
          <ShieldCheck size={14} className="text-emerald-600" /> KYC Details
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div><Label className="flex items-center gap-1.5"><FileBadge size={12} /> PAN <span className="text-red-500">*</span></Label><Input value={form.pan} onChange={e => set('pan', e.target.value.toUpperCase())} placeholder="ABCDE1234F" maxLength={10} data-testid="profile-pan" /></div>
          <div><Label className="flex items-center gap-1.5"><FileBadge size={12} /> Aadhaar <span className="text-red-500">*</span></Label><Input value={form.aadhaar} onChange={e => set('aadhaar', e.target.value)} placeholder="12-digit Aadhaar" maxLength={12} data-testid="profile-aadhaar" /></div>
          <div className="md:col-span-2"><Label className="flex items-center gap-1.5"><MapPin size={12} /> Address <span className="text-red-500">*</span></Label><Input value={form.address} onChange={e => set('address', e.target.value)} placeholder="Full residential address" data-testid="profile-address" /></div>
          <div>
            <Label className="flex items-center gap-1.5"><Droplet size={12} /> Blood Group <span className="text-red-500">*</span></Label>
            <Select value={form.blood_group} onValueChange={v => set('blood_group', v)}>
              <SelectTrigger data-testid="profile-blood"><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>
                {BLOOD_GROUPS.map(b => <SelectItem key={b} value={b}>{b}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div><Label className="flex items-center gap-1.5"><Phone size={12} /> Emergency Contact</Label><Input value={form.emergency_contact} onChange={e => set('emergency_contact', e.target.value)} placeholder="Name" data-testid="profile-emerg-name" /></div>
          <div className="md:col-span-2"><Label className="flex items-center gap-1.5"><Phone size={12} /> Emergency Phone <span className="text-red-500">*</span></Label><PhoneInput value={form.emergency_phone} onChange={v => set('emergency_phone', v)} required testid="profile-emerg-phone" /></div>
        </div>
      </div>

      <div className="flex items-center justify-end gap-2 sticky bottom-0 bg-slate-50 dark:bg-slate-950 py-3">
        <Button variant="outline" onClick={() => navigate(-1)} disabled={saving} data-testid="profile-cancel">Cancel</Button>
        <Button onClick={handleSave} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="profile-save">
          {saving ? (<><Loader2 className="animate-spin mr-2" size={14} /> Saving…</>) : 'Save Changes'}
        </Button>
      </div>
    </div>
  );
};

export default MyProfilePage;
